<template>

  <shell :title="$t('titles.your_profile')">

    <v-form @submit.prevent="submit" novalidate>

      <p class="mb-8">{{ $t('alerts.profile_form_instructions') }}
        
      </p>

      <o-text-field
        :label="$t('titles.first_name')"
        v-model="v$.firstName.$model"
        :validator="v$.firstName"
        autofocus
        required
      />

      <o-text-field
        :label="$t('titles.last_name')"
        v-model="v$.lastName.$model"
        :validator="v$.lastName"
        required
      />

      <o-text-field
        :label="$t('titles.username')"
        v-model="v$.username.$model"
        :validator="v$.username"
        required
      />

      <o-text-field
        :label="$t('titles.email_address')"
        type="email"
        v-model="v$.email.$model"
        :validator="v$.email"
        required
      />

      <o-text-field
        :label="$t('titles.phone_number')"
        type="tel"
        v-model="v$.phone.$model"
        :validator="v$.phone"
        required
      />

      <v-checkbox
        v-if="hasReportingSolutionForAtLeastOneLocation"
        :label="$t('titles.receive_daily_summary_emails')"
        v-model="formState.receiveSummaryEmails"
      />

      <o-text-field
        :label="$t('titles.organization')"
        readonly
        :model-value="userOrganizationName"
      />

      <v-btn
        type="submit"
        prepend-icon="$confirm"
        :loading="submitting"
        :disabled="submitting"
      >
        {{ $t('actions.save') }}
      </v-btn>

    </v-form>

  </shell>

</template>

<script setup lang="ts">

import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'

import store from '../../plugins/vuex'
import Location from '../../models/Location'
import User from '../../models/User'
import Organization from '../../models/Organization'
import Shell from './Shell.vue'

// validation
import useVuelidate from '@vuelidate/core'
import { required, email } from '../../utils/i18n-validators'

// misc
import NotificationService from '../../services/NotificationService'
import type { UpdateUserRequest } from '../../models/User'

const { t } = useI18n()
const title = useTitle()
title.value = t('titles.your_profile')

const userOrganizationName = computed(() => {
  let name = t('misc.unknown_item')
  const organization = Organization.find(store.state.auth.user.organizationID)
  if (organization) {
    name = organization.name
  }
  return name
})

const hasReportingSolutionForAtLeastOneLocation = computed(() => {
  return Location.getOptions('reporting', 'read', null, true).length > 0
})

/**
 * Form state and rules
 */

const user = store.state.auth.user

const formState = reactive({
  firstName: user.firstName,
  lastName: user.lastName,
  username: user.username,
  email: user.email,
  phone: user.phone,
  receiveSummaryEmails: user.receiveSummaryEmails,
})

const formRules = {
  firstName: {
    required,
  },
  lastName: {
    required,
  },
  username: {
    required,
  },
  email: {
    required,
    email,
  },
  phone: {
    required
  },
}

const v$ = useVuelidate(formRules, formState)

/**
 * Submit handler
 */

const submitting = ref(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid) {
    return
  }

  submitting.value = true

  const payload: UpdateUserRequest = {
    ID: store.state.auth.user.ID,
    firstName: formState.firstName,
    lastName: formState.lastName,
    email: formState.email,
    phone: formState.phone,
    receiveSummaryEmails: formState.receiveSummaryEmails,
  }

  User
    .saveExisting(payload)
    .then(() => {
      const updatedUser = User.find(store.state.auth.user.ID)
      store.commit('auth/updateUser', updatedUser)
      NotificationService.success(t('alerts.profile_updated'))
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_update_profile'))
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
